import { useMemo } from 'react';
import { matchEnum, UISerializableOrder } from '@drift/common';
import {
	OrderTriggerCondition,
	OrderType,
	PositionDirection,
} from '@drift-labs/sdk';
import { isEntirePositionOrder } from 'src/utils/fullPositionOrders';
import { useCurrentAccountOrders } from './query/accountTradingData/useOrders';

export type TpSlOrdersByMarket = {
	takeProfitPositionOrder?: UISerializableOrder;
	stopLossPositionOrder?: UISerializableOrder;
	takeProfitPartialOrders: UISerializableOrder[];
	stopLossPartialOrders: UISerializableOrder[];
};

export const useTpSlOrdersByMarket = (
	marketIndex: number | undefined,
	isLong: boolean
): TpSlOrdersByMarket => {
	const allOrders = useCurrentAccountOrders();

	return useMemo(() => {
		let takeProfitPositionOrder: UISerializableOrder | undefined;
		let stopLossPositionOrder: UISerializableOrder | undefined;
		const tpPartialOrders: UISerializableOrder[] = [];
		const slPartialOrders: UISerializableOrder[] = [];

		allOrders.forEach((order) => {
			if (order.marketIndex !== marketIndex) return;

			// Any order in the same direction is not a TP/SL order
			const isOppositeDirection =
				(isLong && matchEnum(order.direction, PositionDirection.SHORT)) ||
				(!isLong && matchEnum(order.direction, PositionDirection.LONG));
			if (!isOppositeDirection) return;

			// // Limit orders in opposite direction are always TP orders, if limit price is below the current mark price it will be filled immediately
			// const isLimitOrder = matchEnum(order.orderType, OrderType.LIMIT);
			// if (isLimitOrder && props.type === 'takeProfit') return true;

			// Trigger orders are TP or SL depending on what side of the oracle price they are on
			const isTriggerOrder =
				matchEnum(order.orderType, OrderType.TRIGGER_MARKET) ||
				matchEnum(order.orderType, OrderType.TRIGGER_LIMIT);
			// All orther order types should not appear here
			if (!isTriggerOrder) {
				return;
			}

			const orderIsFullPosition = isEntirePositionOrder(order.baseAssetAmount);

			const orderIsTpForLong =
				isLong &&
				matchEnum(order.triggerCondition, OrderTriggerCondition.ABOVE);
			const orderIsTpForShort =
				!isLong &&
				matchEnum(order.triggerCondition, OrderTriggerCondition.BELOW);
			if (orderIsTpForLong || orderIsTpForShort) {
				if (orderIsFullPosition) {
					takeProfitPositionOrder = order;
				} else {
					tpPartialOrders.push(order);
				}
			}

			const orderIsSlForLong =
				isLong &&
				matchEnum(order.triggerCondition, OrderTriggerCondition.BELOW);
			const orderIsSlForShort =
				!isLong &&
				matchEnum(order.triggerCondition, OrderTriggerCondition.ABOVE);
			if (orderIsSlForLong || orderIsSlForShort) {
				if (orderIsFullPosition) {
					stopLossPositionOrder = order;
				} else {
					slPartialOrders.push(order);
				}
			}
		});
		const takeProfitPartialOrders = tpPartialOrders.sort((a, b) => {
			return a.triggerPrice.val.sub(b.triggerPrice.val).toNumber();
		});
		const stopLossPartialOrders = slPartialOrders.sort((a, b) => {
			return b.triggerPrice.val.sub(a.triggerPrice.val).toNumber();
		});

		return {
			takeProfitPositionOrder,
			stopLossPositionOrder,
			takeProfitPartialOrders,
			stopLossPartialOrders,
		};
	}, [allOrders, marketIndex, isLong]);
};
